import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User, Family, Task, Reward } from 'shared/types';
import {
  login as apiLogin,
  register as apiRegister,
  getCurrentUser as apiGetCurrentUser,
  getFamily as apiGetFamily,
  createTask as apiCreateTask,
  updateTask as apiUpdateTask,
  deleteTask as apiDeleteTask,
  claimTask as apiClaimTask,
  unclaimTask as apiUnclaimTask,
  submitTaskForVerification as apiSubmitTaskForVerification,
  verifyTask as apiVerifyTask,
  ackTaskVerification as apiAckTaskVerification,
  createReward as apiCreateReward,
  updateReward as apiUpdateReward,
  deleteReward as apiDeleteReward,
  claimReward as apiClaimReward,

  createTool as apiCreateTool,
  updateTool as apiUpdateTool,
  deleteTool as apiDeleteTool,
  createRoom as apiCreateRoom,
  updateRoom as apiUpdateRoom,
  deleteRoom as apiDeleteRoom,
  initializeAuth,
  clearAuth,
  restoreSupabaseSession,
  setCachedFamilyId,
  deviceBind as apiDeviceBind,
  getDeviceSession as apiGetDeviceSession,
  memberLoginApi,
  memberLogoutApi,
  deviceLogout as apiDeviceLogout,
  deviceUnlock as apiDeviceUnlock,
  updateFamilyMember as apiUpdateFamilyMember
} from '../services/apiService';
import type { VerifyTaskOptions, VerifyTaskResult } from '../services/apiService';
import { storageService } from '../services/storageService';
import { supabase, clearPinMemberSession, hasPinMemberSessionFlag, isMemberMode } from '../services/supabaseClient';
import { isMemberIdleExpired, markMemberActive, readMemberLastActive, clearMemberLastActive } from '../utils/memberIdle';
import { mayRestoreParkedLogin } from '../utils/switchUser';

export interface AppContextType {
  // User state
  current_user: User | null;
  current_family: Family | null;
  is_authenticated: boolean;
  is_device_bound?: boolean;
  
  // Device members for chooser
  device_members?: Array<{ id: string; name: string; username: string | null; is_admin: boolean; member_type: 'adult' | 'child' }>;
  
  // Auth functions
  login: (email: string, password: string) => Promise<void>;
  bindDevice: (deviceName?: string) => Promise<void>;
  memberLogin: (username: string, pin_or_password: string) => Promise<void>;
  switchUser: () => Promise<void>;
  /**
   * End a PIN member's turn and show "Who are you?" WITHOUT restoring a parked guardian session
   * (switchUser would — a child must never land in a parent's account). Used by the child's menu.
   */
  endMemberTurn: () => void;
  register: (name: string, email: string, password: string, familyName: string) => Promise<void>;
  logout: () => void;
  deviceLogout?: () => Promise<void>;
  deviceUnlock?: () => Promise<void>;
  refreshDeviceSession?: () => Promise<void>;
  
  // Data management
  refreshFamilyData: () => Promise<void>;
  /** Load the next page of older finished tasks (get-family only sends the most recent ones). */
  loadMoreCompletedTasks: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  
  // Task functions
  createTask: (taskData: any) => Promise<void>;
  updateTask: (id: string, updates: any) => Promise<void>;
  deleteTask: (id: string, opts?: { wholeGroup?: boolean }) => Promise<void>;
  claimTask: (id: string) => Promise<void>;
  unclaimTask: (id: string) => Promise<void>;
  submitTaskForVerification: (id: string, completion_message: string) => Promise<{ auto_approved: boolean }>;
  verifyTask: (id: string, opts?: VerifyTaskOptions) => Promise<VerifyTaskResult | void>;
  ackTaskVerification: (id: string) => Promise<void>;

  // Reward functions
  createReward: (rewardData: any) => Promise<void>;
  updateReward: (id: string, updates: any) => Promise<void>;
  deleteReward: (id: string) => Promise<void>;
  claimReward: (id: string, message: string) => Promise<void>;
  
  // Room functions
  createRoom: (roomData: any) => Promise<void>;
  updateRoom: (id: string, updates: any) => Promise<void>;
  deleteRoom: (id: string) => Promise<void>;
  
  // Tool functions
  createTool: (toolData: any) => Promise<void>;
  updateTool: (id: string, updates: any) => Promise<void>;
  deleteTool: (id: string) => Promise<void>;
  
  // Utility functions
  updateCurrentUser: (updates: Partial<User>) => void;
  // Member management
  updateFamilyMember: (id: string, updates: { member_type?: 'adult' | 'child'; name?: string; pin?: string; is_admin?: boolean }) => Promise<void>;
}

/** Mirrors DEFAULT_COMPLETED_LIMIT / COMPLETED_PAGE_SIZE in supabase/functions/_shared/taskWindow.ts. */
export const COMPLETED_TASKS_DEFAULT_LIMIT = 20;
export const COMPLETED_TASKS_PAGE_SIZE = 20;

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [current_user, setCurrentUser] = useState<User | null>(null);
  const [current_family, setCurrentFamily] = useState<Family | null>(null);
  const [is_authenticated, setIsAuthenticated] = useState(false);
  const [is_device_bound, setIsDeviceBound] = useState<boolean>(false);
  const [device_members, setDeviceMembers] = useState<Array<{ id: string; name: string; username: string | null; is_admin: boolean; member_type: 'adult' | 'child' }>>([]);
  // How many finished tasks get-family should include. A ref (not state) so every existing
  // refreshFamilyData() call keeps the history the user has expanded with "show more".
  const completedLimitRef = useRef(COMPLETED_TASKS_DEFAULT_LIMIT);

  // Initialize app on mount
  useEffect(() => {
    initializeApp();
    // Listen for centralized auth-required events (401/403)
    const onAuthRequired = (e: Event) => {
      try {
        storageService.removeAuthToken();
        clearAuth().catch(() => {});
        setCurrentUser(null);
        setCurrentFamily(null);
        setIsAuthenticated(false);
      } catch {}
    };
    const onAuthRequiredWithPin = (e: Event) => {
      clearPinMemberSession();
      onAuthRequired(e);
    };
    window.addEventListener('ftm:auth-required', onAuthRequiredWithPin as EventListener);
    return () => {
      window.removeEventListener('ftm:auth-required', onAuthRequiredWithPin as EventListener);
    };
  }, []);

  // ── Idle lock for PIN members (docs/design/pin-member-auth.md, D1 = b) ──
  // Same outcome as the old 1-hour token expiry (back to "who are you?", guardian session stays
  // parked), but only after 60 minutes WITHOUT activity — active use is never interrupted.
  const lockMemberSession = () => {
    storageService.removeAuthToken();
    clearPinMemberSession();
    clearMemberLastActive();
    setCurrentUser(null);
    setCurrentFamily(null);
    completedLimitRef.current = COMPLETED_TASKS_DEFAULT_LIMIT;
    setIsAuthenticated(false);
    refreshDeviceSession();
  };

  useEffect(() => {
    if (!is_authenticated || !isMemberMode()) return;
    if (readMemberLastActive() == null) markMemberActive();
    let lastWrite = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite > 30_000) { // throttle storage writes
        lastWrite = now;
        markMemberActive(now);
      }
    };
    const check = () => {
      if (isMemberMode() && isMemberIdleExpired(readMemberLastActive(), Date.now())) lockMemberSession();
    };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(check, 60_000);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_authenticated, current_user?.id]);

  const initializeApp = async () => {
    try {
      // Member mode: guardian session saved, member token in storage — use member token for API (setSession doesn't persist custom JWTs)
      const memberToken = storageService.getAuthToken();
      const guardianSession = storageService.getGuardianSession();
      const isMember = !!(memberToken && (guardianSession || hasPinMemberSessionFlag()));

      let token: string | null = null;
      if (isMember && isMemberIdleExpired(readMemberLastActive(), Date.now())) {
        // Idle lock (D1 = b): the member left the device for 60+ minutes — back to the chooser,
        // keeping any parked guardian session parked.
        storageService.removeAuthToken();
        clearPinMemberSession();
        clearMemberLastActive();
      } else if (isMember) {
        token = memberToken;
      } else {
        token = await restoreSupabaseSession();
        if (!token) token = memberToken;
      }

      if (token) {
        initializeAuth(token);

        try {
          const user = await apiGetCurrentUser();
          setCurrentUser(user);
          setIsAuthenticated(true);

          // Get family data
          await refreshFamilyData();
        } catch (error) {
          console.error('🔍 initializeApp: getCurrentUser failed, clearing auth:', error);
          storageService.removeAuthToken();
          clearPinMemberSession();
          if (isMember) storageService.removeGuardianSession();
          await clearAuth();
        }
      }
      // Device session requires a bound device; skip on OAuth callback (no session yet → avoids noisy 401)
      const onOAuthCallback =
        typeof window !== 'undefined' && window.location.pathname.startsWith('/oauth/callback');
      if (!onOAuthCallback) {
        try {
          const ds = await apiGetDeviceSession();
          setIsDeviceBound(true);
          setDeviceMembers(ds.members);
          (window as any).__ds_username_locks = ds.username_locks || [];
        } catch (e) {
          setIsDeviceBound(false);
          setDeviceMembers([]);
        }
      } else {
        setIsDeviceBound(false);
        setDeviceMembers([]);
      }
    } catch (error) {
      console.error('🔍 initializeApp: Initialization failed:', error);
    }
  };

  const login = async (email: string, password: string) => {
    const { token, user } = await apiLogin(email, password);
    storageService.setAuthToken(token);
    initializeAuth(token);
    setCurrentUser(user);
    setIsAuthenticated(true);
    // After guardian login, attempt to bind device silently
    try {
      await apiDeviceBind();
      const ds = await apiGetDeviceSession();
      setIsDeviceBound(true);
      setDeviceMembers(ds.members);
    } catch (e) {
      console.warn('Device bind failed or not applicable:', e);
      setIsDeviceBound(false);
    }
    await refreshFamilyData();
  };

  const bindDevice = async (deviceName?: string) => {
    await apiDeviceBind(deviceName);
    const ds = await apiGetDeviceSession();
    setIsDeviceBound(true);
    setDeviceMembers(ds.members);
    (window as any).__ds_username_locks = ds.username_locks || [];
  };

  const memberLogin = async (username: string, pin_or_password: string) => {
    const { user } = await memberLoginApi(username, pin_or_password);
    markMemberActive();
    setCurrentUser(user);
    setIsAuthenticated(true);
    if (user?.family_id) setCachedFamilyId(user.family_id);
    await refreshFamilyData();
    try {
      await refreshUserData();
    } catch {
      /* non-fatal — member-auth user may still be current */
    }
  };

  const switchUser = async () => {
    const whoWasHere = current_user?.id ?? null;
    try {
      await memberLogoutApi();
    } catch (e) {
      console.warn('memberLogoutApi failed or not necessary:', e);
    }
    clearPinMemberSession();
    storageService.removeAuthToken();
    setCurrentUser(null);
    setCurrentFamily(null);
    completedLimitRef.current = COMPLETED_TASKS_DEFAULT_LIMIT;
    setIsAuthenticated(false);

    // Restore the parked guardian login only for its owner (utils/switchUser). Anyone else just
    // ends their turn: "Who are you?" with the login still parked.
    const guardianSession = storageService.getGuardianSession();
    if (guardianSession?.access_token && !mayRestoreParkedLogin(guardianSession, whoWasHere)) {
      clearMemberLastActive();
      await refreshDeviceSession();
      return;
    }
    if (guardianSession?.access_token) {
      await supabase.auth.setSession({
        access_token: guardianSession.access_token,
        refresh_token: guardianSession.refresh_token,
      });
      storageService.removeGuardianSession();
      storageService.setAuthToken(guardianSession.access_token);
      initializeAuth(guardianSession.access_token);
    } else {
      // We're guardian switching to member — save our session before MemberChooser
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        storageService.setGuardianSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token ?? data.session.access_token,
          user: data.session.user ? { id: data.session.user.id } : undefined,
        });
      }
      initializeAuth('');
    }

    await refreshDeviceSession();
  };

  const refreshDeviceSession = async () => {
    try {
      const ds = await apiGetDeviceSession();
      setIsDeviceBound(true);
      setDeviceMembers(ds.members);
      (window as any).__ds_username_locks = ds.username_locks || [];
    } catch (e) {
      setIsDeviceBound(false);
      setDeviceMembers([]);
    }
  };

  const register = async (name: string, email: string, password: string, familyName: string) => {
    const { token, user } = await apiRegister(name, email, password, familyName);
    storageService.setAuthToken(token);
    initializeAuth(token);
    setCurrentUser(user);
    setIsAuthenticated(true);
    await refreshFamilyData();
  };

  const logout = async () => {
    storageService.removeAuthToken();
    await clearAuth();
    setCurrentUser(null);
    setCurrentFamily(null);
    completedLimitRef.current = COMPLETED_TASKS_DEFAULT_LIMIT;
    setIsAuthenticated(false);
    setCachedFamilyId(null);
    refreshDeviceSession();
  };

  const deviceLogout = async () => {
    try {
      await apiDeviceLogout();
    } catch (e) {
      console.warn('apiDeviceLogout failed or not necessary:', e);
    }
    clearPinMemberSession();
    storageService.removeAuthToken();
    storageService.removeGuardianSession();
    await clearAuth();
    setCurrentUser(null);
    setCurrentFamily(null);
    completedLimitRef.current = COMPLETED_TASKS_DEFAULT_LIMIT;
    setIsAuthenticated(false);
    setIsDeviceBound(false);
    setDeviceMembers([]);
    setCachedFamilyId(null);
  };

  const deviceUnlock = async () => {
    await apiDeviceUnlock();
    try {
      const ds = await apiGetDeviceSession();
      setIsDeviceBound(true);
      setDeviceMembers(ds.members);
      (window as any).__ds_username_locks = ds.username_locks || [];
    } catch (e) {
      setIsDeviceBound(false);
      setDeviceMembers([]);
    }
  };

  const refreshFamilyData = async () => {
    try {
      const family = await apiGetFamily(completedLimitRef.current);
      setCurrentFamily(family);
      setCachedFamilyId(family?.id ?? null);
    } catch (error) {
      console.error('Failed to refresh family data:', error);
      setCurrentFamily(null);
      setCachedFamilyId(null);
    }
  };

  const loadMoreCompletedTasks = async () => {
    completedLimitRef.current += COMPLETED_TASKS_PAGE_SIZE;
    await refreshFamilyData();
  };

  const refreshUserData = async () => {
    try {
      const user = await apiGetCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      setCurrentUser(null);
    }
  };

  const updateCurrentUser = (updates: Partial<User>) => {
    if (current_user) {
      setCurrentUser({ ...current_user, ...updates });
    }
  };

  // Task functions
  // Mutations below don't log: they throw to the screen that called them, which shows the error
  // (logging here too printed every failure twice — plan, Phase 1 notes).
  const createTask = async (taskData: any) => {
    await apiCreateTask(taskData);
    await refreshFamilyData();
  };

  const updateTask = async (id: string, updates: any) => {
    await apiUpdateTask(id, updates);
    await refreshFamilyData();
  };

  const deleteTask = async (id: string, opts?: { wholeGroup?: boolean }) => {
    await apiDeleteTask(id, opts);
    await refreshFamilyData();
  };

  const claimTask = async (id: string) => {
    await apiClaimTask(id);
    await refreshFamilyData();
  };

  const unclaimTask = async (id: string) => {
    await apiUnclaimTask(id);
    await refreshFamilyData();
  };

  const submitTaskForVerification = async (id: string, completion_message: string) => {
    const result = await apiSubmitTaskForVerification(id, completion_message);
    if (result.auto_approved) {
      // A grown-up's own task, approved at once (2026-10-19): their stars changed too.
      await Promise.all([
        refreshFamilyData(),
        apiGetCurrentUser()
          .then((updatedUser) => setCurrentUser(updatedUser))
          .catch((error) => console.error('submitTask: could not refresh the current user', error)),
      ]);
    } else {
      await refreshFamilyData();
    }
    return result;
  };

  const verifyTask = async (id: string, opts?: VerifyTaskOptions) => {
    const verifyResult = await apiVerifyTask(id, opts);

    // One family load (task status + every member's stars) and the current user, side by side.
    // There used to be a third, delayed family load "for UI consistency"; it was redundant and
    // multiplied when several tasks were approved in a row (plan: Known bugs, 2026-10-12).
    await Promise.all([
      refreshFamilyData(),
      apiGetCurrentUser()
        .then((updatedUser) => setCurrentUser(updatedUser))
        .catch((error) => console.error('verifyTask: could not refresh the current user', error)),
    ]);

    return verifyResult;
  };

  const ackTaskVerification = async (id: string) => {
    await apiAckTaskVerification(id);
    await refreshFamilyData();
  };

  // Reward functions
  const createReward = async (rewardData: any) => {
    await apiCreateReward(rewardData);
    await refreshFamilyData();
  };

  const updateReward = async (id: string, updates: any) => {
    await apiUpdateReward(id, updates);
    await refreshFamilyData();
  };

  const deleteReward = async (id: string) => {
    await apiDeleteReward(id);
    await refreshFamilyData();
  };

  const claimReward = async (id: string, message: string) => {
    await apiClaimReward(id);
    await refreshFamilyData();
    await refreshUserData(); // Refresh user data to get updated points
  };



  // Room functions
  const createRoom = async (roomData: any) => {
    await apiCreateRoom(roomData);
    await refreshFamilyData();
  };

  const updateRoom = async (id: string, roomData: any) => {
    await apiUpdateRoom(id, roomData);
    await refreshFamilyData();
  };

  const deleteRoom = async (id: string) => {
    await apiDeleteRoom(id);
    await refreshFamilyData();
  };

  // Tool functions
  const createTool = async (toolData: any) => {
    await apiCreateTool(toolData);
    await refreshFamilyData();
  };

  const updateTool = async (id: string, updates: any) => {
    await apiUpdateTool(id, updates);
    await refreshFamilyData();
  };

  const deleteTool = async (id: string) => {
    await apiDeleteTool(id);
    await refreshFamilyData();
  };

  // Member management
  const updateFamilyMember = async (
    id: string,
    updates: { member_type?: 'adult' | 'child'; name?: string; pin?: string; is_admin?: boolean }
  ) => {
    await apiUpdateFamilyMember(id, updates as any);
    await refreshFamilyData();
  };

  const contextValue: AppContextType = {
    // User state
    current_user,
    current_family,
    is_authenticated,
    is_device_bound,
    device_members,
    
    // Auth functions
    login,
    bindDevice,
    memberLogin,
    switchUser,
    endMemberTurn: lockMemberSession,
    register,
    logout,
    deviceLogout,
    deviceUnlock,
    
    // Data management
    refreshFamilyData,
    loadMoreCompletedTasks,
    refreshUserData,
    
    // Task functions
    createTask,
    updateTask,
    deleteTask,
    claimTask,
    unclaimTask,
    submitTaskForVerification,
    verifyTask,
    ackTaskVerification,
    
    // Reward functions
    createReward,
    updateReward,
    deleteReward,
    claimReward,
    
    // Room functions
    createRoom,
    updateRoom,
    deleteRoom,
    
    // Tool functions
    createTool,
    updateTool,
    deleteTool,
    
    // Utility functions
    updateCurrentUser,
    refreshDeviceSession,
    
    // Member management
    updateFamilyMember,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};
